import {
  AfterViewChecked,
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  Inject,
  OnDestroy,
  Signal,
  ViewChild,
  computed,
  createComponent,
  createEnvironmentInjector,
  effect,
  inject,
  signal
} from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import {
  PopupButton,
  PopupComponent
} from "src/app/services/popup/popup-component";
import { sntTranslate } from "src/app/support/translate";
import {
  PopupWizardStepComponent,
  PopupWizardStepConfig
} from "src/app/services/popup/popup-wizard/popup-wizard.step";
import { POPUP_INJECT, PopupRef } from "src/app/services/popup/popup-ref";
import { isUndefined } from "lodash";

@Component({
  imports: [],
  templateUrl: "popup-wizard.component.html"
})
export class PopupWizardComponent
  implements OnDestroy, AfterViewInit, PopupComponent
{
  @ViewChild("stepContainer")
  stepContainer?: ElementRef<HTMLDivElement>;

  state = signal<any>({});
  steps = signal<PopupWizardStepConfig[]>([]);

  selectedIndex = signal<number>(0);

  stepHeight = computed<number | undefined>(() => {
    const index = this.selectedIndex();
    const steps = this.steps();

    const currentStep = steps[index];
    if (!currentStep) {
      return undefined;
    }

    return currentStep.height;
  });

  componentRef = computed<ComponentRef<PopupWizardStepComponent> | null>(() => {
    const index = this.selectedIndex();
    const steps = this.steps();
    const state = this.state();

    if (steps.length === 0) {
      return null;
    }

    const step = steps[index];

    const componentRef = createComponent<PopupWizardStepComponent>(
      step.component,
      {
        environmentInjector: createEnvironmentInjector(
          [
            { provide: POPUP_INJECT.WIZARD_STATE, useValue: state },
            {
              provide: POPUP_INJECT.WIZARD_NEXT_FN,
              useValue: () => {
                this.destroyCurrentStep();

                this.selectedIndex.update((value) =>
                  Math.min(this.steps().length - 1, value + 1)
                );
              }
            }
          ],
          this.environmentInjector
        )
      }
    );

    return componentRef;
  });

  buttons = computed<PopupButton[]>(() => {
    const selectedIndex = this.selectedIndex();
    const steps = this.steps();

    const buttons: PopupButton[] = [];
    if (selectedIndex === steps.length - 1) {
      if (!!this.componentRef()?.instance.isCancelButtonVisible()) {
        buttons.push(<PopupButton>{
          title:
            (this.componentRef()?.instance.getCancelButtonTitle &&
              this.componentRef()?.instance.getCancelButtonTitle!()) ||
            sntTranslate(marker("Annulla")),
          type: "text",
          onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
            await buttonClickAsyncEvent.execute(async () => {
              await this.popupRef.close();
            });
          }
        });
      }
      if (!!this.componentRef()?.instance.isBackButtonVisible()) {
        buttons.push({
          title:
            (this.componentRef()?.instance.getBackButtonTitle &&
              this.componentRef()?.instance.getBackButtonTitle!()) ||
            sntTranslate(marker("Indietro")),
          type: "outlined",
          onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
            this.onPrev();
          }
        });
      }

      buttons.push({
        title:
          (this.componentRef()?.instance.getPrimaryButtonTitle &&
            this.componentRef()?.instance.getPrimaryButtonTitle!()) ||
          sntTranslate(marker("Concludi")),
        type: "primary",
        onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
          await this.onComplete(buttonClickAsyncEvent);
        }
      });

      return buttons;
    }

    if (!!this.componentRef()?.instance.isCancelButtonVisible()) {
      buttons.push(<PopupButton>{
        title:
          (this.componentRef()?.instance.getCancelButtonTitle &&
            this.componentRef()?.instance.getCancelButtonTitle!()) ||
          sntTranslate(marker("Annulla")),
        type: "text",
        onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
          await buttonClickAsyncEvent.execute(async () => {
            await this.popupRef.close();
          });
        }
      });
    }

    if (!!this.componentRef()?.instance.isBackButtonVisible()) {
      buttons.push({
        disabled: selectedIndex === 0,
        title:
          (this.componentRef()?.instance.getBackButtonTitle &&
            this.componentRef()?.instance.getBackButtonTitle!()) ||
          sntTranslate(marker("Indietro")),
        type: "outlined",
        onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
          this.onPrev();
        }
      });
    }

    buttons.push({
      title:
        (this.componentRef()?.instance.getPrimaryButtonTitle &&
          this.componentRef()?.instance.getPrimaryButtonTitle!()) ||
        sntTranslate(marker("Continua")),
      type: "primary",
      onClick: async (buttonClickAsyncEvent: ButtonClickAsyncEvent) => {
        await this.onNext(buttonClickAsyncEvent);
      }
    });

    return buttons;
  });

  title = computed<string>(() => {
    const selectedIndex = this.selectedIndex();
    const steps = this.steps();

    if (steps.length === 0) {
      return "";
    }

    const currentStep = steps[selectedIndex];
    if (!currentStep) {
      return "";
    }

    return currentStep.title;
  });

  stepperProgress = computed<Array<{ active: boolean; done: boolean }>>(() => {
    const selectedIndex = this.selectedIndex();
    const steps = this.steps();

    const ret: Array<{ active: boolean; done: boolean }> = [];

    steps.forEach((step, index) => {
      if (index > selectedIndex) {
        ret.push({ active: false, done: false });
      }

      if (index === selectedIndex) {
        ret.push({ active: true, done: false });
      }

      if (index < selectedIndex) {
        ret.push({ done: true, active: false });
      }
    });

    return ret;
  });

  indexOfActiveStep = computed<number>(() => {
    const steps = this.stepperProgress();
    return steps.findIndex((x) => x.active);
  });

  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);

  constructor(
    @Inject(POPUP_INJECT.REF) private popupRef: PopupRef<any>,
    @Inject(POPUP_INJECT.DATA)
    private data: {
      state: any;
      steps: PopupWizardStepConfig[];
      onCreate: (state: any) => Promise<void>;
    }
  ) {
    this.state.set(data.state);
    this.steps.set(data.steps);

    effect(() => {
      this.syncComponentRefWithUI();
    });
  }

  ngAfterViewInit(): void {
    this.syncComponentRefWithUI();
  }

  ngOnDestroy(): void {
    this.destroyCurrentStep();
  }

  getTitle(): string {
    return this.title();
  }

  private syncComponentRefWithUI(): void {
    const componentRef = this.componentRef();
    if (componentRef) {
      this.stepContainer?.nativeElement.append(
        componentRef.location.nativeElement
      );
      this.appRef.attachView(componentRef.hostView);

      const currentStepHeight = this.stepHeight();

      if (this.popupRef.updateHeight && !isUndefined(currentStepHeight)) {
        this.popupRef.updateHeight(currentStepHeight);
      }
    }
  }

  onPrev(): void {
    this.destroyCurrentStep();

    this.selectedIndex.update((value) => Math.max(0, value - 1));
  }

  async onNext(buttonClickAsyncEvent: ButtonClickAsyncEvent): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await this.checkCurrentStepIsValid();

      this.destroyCurrentStep();

      this.selectedIndex.update((value) =>
        Math.min(this.steps().length - 1, value + 1)
      );
    });
  }

  async onComplete(
    buttonClickAsyncEvent: ButtonClickAsyncEvent
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await this.checkCurrentStepIsValid();

      await this.data.onCreate(this.data.state);

      await this.popupRef.close();
    });
  }

  private async checkCurrentStepIsValid(): Promise<void> {
    const validationResult = await this.componentRef()?.instance.validate();
    if (!validationResult?.isValid) {
      throw new Error(
        validationResult?.message ||
          (validationResult?.noMessage
            ? ""
            : sntTranslate(marker("Alcuni campi non sono validi.")))
      );
    }
  }

  private destroyCurrentStep(): void {
    const componentRef = this.componentRef();
    componentRef?.destroy();
  }
}
